import { useMemo, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  ChevronRight,
  FileText,
  Hourglass,
  Inbox,
  Radio,
} from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VBadge,
  VButton,
  VKpiCard,
} from "@/components/ui";
import type { Dossier, ReservationRequest } from "@/v2/shared/model";
import { daysUntil, formatPeriod } from "@/v2/shared/format";
import { DossierStatusBadge, RequestStatusBadge } from "@/v2/shared/statusBadges";
import { findAsset } from "@/v2/data/db";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useRequests } from "@/v2/data/queries";

const SIGNAL_WINDOW_DAYS = 7;

// Binnen dit venster geldt een inzet-deadline (plaatsing of ophaling) als
// dringend; sluit aan op het urgent-accent uit CB-40.
const URGENT_DAYS = 2;

// Lijsten op het dashboard tonen maximaal dit aantal rijen; het totaal staat
// in de telbadge naast de paneltitel en de rest zit achter de doorklik.
const PANEL_LIMIT = 5;

type DashboardSignal = {
  requestId: string;
  title: string;
  number: string;
  assetNumber: string;
  period: string;
  days: number;
};

// Eén rij in "Snel op te volgen": een deadline die nadert of verstreken is
// terwijl de bijhorende actie nog ontbreekt. Voorstel-regels (CB-40, nog af
// te stemmen met de PO's).
type AttentionItem = {
  key: string;
  to: string;
  title: string;
  number: string;
  reason: string;
  timing: string;
  days: number;
};

// Het dashboard is een beheerder-story (CB-40); de aanvrager landt op zijn
// aanvragenoverzicht (CB-8).
export function DashboardPage(): React.JSX.Element {
  const user = useCurrentUser();
  if (user.role !== "manager") return <Navigate to="/aanvragen" replace />;
  return <ManagerDashboard />;
}

function ManagerDashboard(): React.JSX.Element {
  const navigate = useNavigate();
  const { data, isPending, isError } = useRequests();

  const stats = useMemo(() => {
    const requests = data ?? [];
    const dossiers = requests.flatMap((r) => r.dossiers);
    const deployments = dossiers.flatMap((d) => d.deployments);
    return {
      open: requests.filter((r) => r.status === "OPEN").length,
      onWaitlist: dossiers.filter((d) => d.status === "ON_WAITLIST").length,
      plannedDossiers: dossiers.filter((d) => d.status === "PLANNED").length,
      deployed: deployments.filter((dep) => dep.status === "DEPLOYED").length,
      plannedDeployments: deployments.filter((dep) => dep.status === "PLANNED").length,
      // Oudste eerst: wat het langst wacht, hoort bovenaan de top-5.
      needsAction: [...requests.filter((r) => r.status === "OPEN")].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      ),
      // Op dossierniveau (PZAO-30): het paneel toont het wachtrij-dossier zelf,
      // met zijn toegekende periode en status, gesorteerd op toegekende start.
      waitlist: requests
        .flatMap((r) =>
          r.dossiers
            .filter((d) => d.status === "ON_WAITLIST")
            .map((d) => ({ request: r, dossier: d })),
        )
        .sort((a, b) => a.dossier.startDate.localeCompare(b.dossier.startDate)),
    };
  }, [data]);

  // CB-40: signalen voor opvolging — afgeleid uit de inzetten (verwachte
  // periodes) van geplande en geplaatste inzet.
  const signals = useMemo(() => {
    const active = (data ?? []).flatMap((r) =>
      r.dossiers.flatMap((d) =>
        d.deployments
          .filter((dep) => dep.status !== "CLOSED")
          .map((dep) => ({ request: r, dep })),
      ),
    );
    const toSignal = (
      request: ReservationRequest,
      dep: { assetId: string; expectedStart: string; expectedEnd: string },
      days: number,
    ): DashboardSignal => ({
      requestId: request.id,
      title: request.title,
      number: request.number,
      assetNumber: findAsset(dep.assetId)?.number ?? "-",
      period: formatPeriod(dep.expectedStart, dep.expectedEnd),
      days,
    });

    const expiring = active
      .map(({ request, dep }) => toSignal(request, dep, daysUntil(dep.expectedEnd)))
      .filter((s) => s.days >= 0 && s.days <= SIGNAL_WINDOW_DAYS)
      .sort((a, b) => a.days - b.days);

    const startingSoon = active
      .filter(({ dep }) => dep.status === "PLANNED")
      .map(({ request, dep }) => toSignal(request, dep, daysUntil(dep.expectedStart)))
      .filter((s) => s.days >= 0 && s.days <= SIGNAL_WINDOW_DAYS)
      .sort((a, b) => a.days - b.days);

    return { expiring, startingSoon };
  }, [data]);

  // "Snel op te volgen" (CB-40, voorstel): deadlines die naderen of verstreken
  // zijn terwijl de actie nog ontbreekt. Regels: open aanvraag of wachtrij-
  // dossier waarvan de start binnen 7 dagen valt (of al voorbij is), en
  // inzetten waarvan de verwachte plaatsing of ophaling binnen 2 dagen moet
  // gebeuren (of al voorbij is).
  const attention = useMemo<Array<AttentionItem>>(() => {
    const items: Array<AttentionItem> = [];
    const timing = (label: string, days: number): string => {
      if (days < 0) return `${label} ${-days === 1 ? "1 dag" : `${-days} dagen`} te laat`;
      return `${label} ${daysLabel(days)}`;
    };

    for (const r of data ?? []) {
      if (r.status === "OPEN") {
        const days = daysUntil(r.startDate);
        if (days <= SIGNAL_WINDOW_DAYS) {
          items.push({
            key: `open-${r.id}`,
            to: `/aanvragen/${r.id}`,
            title: r.title,
            number: r.number,
            reason: "Aanvraag nog niet behandeld",
            timing: timing("gevraagde start", days),
            days,
          });
        }
      }
      for (const d of r.dossiers) {
        if (d.status === "ON_WAITLIST") {
          const days = daysUntil(d.startDate);
          if (days <= SIGNAL_WINDOW_DAYS) {
            items.push({
              key: `waitlist-${d.id}`,
              to: `/dossiers/${d.id}`,
              title: r.title,
              number: r.number,
              reason: "Nog geen inzet gepland",
              timing: timing("toegekende start", days),
              days,
            });
          }
        }
        // PZAO-39: alle inzetten afgerond maar het dossier nog niet afgesloten
        // (afronden vergeten) — dossiers sluiten nooit automatisch.
        if (
          d.status !== "CLOSED" &&
          d.deployments.length > 0 &&
          d.deployments.every((dep) => dep.status === "CLOSED")
        ) {
          const lastEnd = d.deployments
            .map((dep) => dep.actualEnd ?? dep.expectedEnd)
            .sort()
            .at(-1);
          const days = lastEnd ? daysUntil(lastEnd) : 0;
          items.push({
            key: `close-${d.id}`,
            to: `/dossiers/${d.id}`,
            title: r.title,
            number: r.number,
            reason: "Alle inzetten afgerond, dossier nog niet afgesloten",
            timing:
              days < 0
                ? `laatste inzet ${-days === 1 ? "1 dag" : `${-days} dagen`} geleden afgerond`
                : "laatste inzet vandaag afgerond",
            days,
          });
        }
        for (const dep of d.deployments) {
          const asset = findAsset(dep.assetId)?.number ?? "-";
          if (dep.status === "PLANNED") {
            const days = daysUntil(dep.expectedStart);
            if (days <= URGENT_DAYS) {
              items.push({
                key: `place-${dep.id}`,
                to: `/dossiers/${d.id}`,
                title: `${asset} · ${r.title}`,
                number: r.number,
                reason: "Plaatsing nog niet geregistreerd",
                timing: timing("verwachte plaatsing", days),
                days,
              });
            }
          }
          if (dep.status === "DEPLOYED") {
            const days = daysUntil(dep.expectedEnd);
            if (days <= URGENT_DAYS) {
              items.push({
                key: `pickup-${dep.id}`,
                to: `/dossiers/${d.id}`,
                title: `${asset} · ${r.title}`,
                number: r.number,
                reason: "Ophaling nog niet geregistreerd",
                timing: timing("verwacht einde", days),
                days,
              });
            }
          }
        }
      }
    }
    return items.sort((a, b) => a.days - b.days);
  }, [data]);

  if (isPending) return <LoadingState label="Dashboard laden…" />;
  if (isError) return <ErrorMessage title="Kon het dashboard niet laden" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Overzicht voor team functionele ondersteuning."
      />

      {/* PZAO-30: elke teller klikt door naar het bijhorende overzicht. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <VKpiCard label="Open aanvragen" value={stats.open} helper="Vereisen behandeling" tone="primary" icon={<Inbox className="size-4" />} onClick={() => navigate("/aanvragen")} />
        <VKpiCard label="Dossiers op wachtrij" value={stats.onWaitlist} helper="Nog geen inzet gepland" tone="warning" icon={<Hourglass className="size-4" />} onClick={() => navigate("/dossiers")} />
        <VKpiCard label="Ingeplande dossiers" value={stats.plannedDossiers} helper="Minstens één inzet gekoppeld" tone="success" icon={<FileText className="size-4" />} onClick={() => navigate("/dossiers")} />
        <VKpiCard label="Inzetten in het veld" value={stats.deployed} helper={`Effectief geplaatst · ${stats.plannedDeployments} nog gepland`} tone="neutral" icon={<Radio className="size-4" />} onClick={() => navigate("/dossiers")} />
      </div>

      {/* CB-40 (voorstel): actielijst met naderende of verstreken deadlines
          waar de bijhorende actie nog ontbreekt. */}
      <Panel
        title="Snel op te volgen"
        count={attention.length}
        icon={<AlertTriangle className="size-4 text-error-600" aria-hidden />}
      >
        {attention.length === 0 ? (
          <PanelEmpty
            title="Niets dringend"
            description="Geen deadlines die naderen zonder dat de actie gebeurd is."
          />
        ) : (
          <>
            <ul className="divide-y divide-border">
              {attention.slice(0, PANEL_LIMIT).map((item) => (
                <ListRow
                  key={item.key}
                  onClick={() => navigate(item.to)}
                  title={item.title}
                  meta={`${item.number} · ${item.reason}`}
                  end={
                    <VBadge color={item.days <= 0 ? "error" : "warning"}>{item.timing}</VBadge>
                  }
                />
              ))}
            </ul>
            <PanelOverflow total={attention.length} />
          </>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Openstaande aanvragen"
          count={stats.needsAction.length}
          action={<PanelLink to="/aanvragen" label="Alle aanvragen" />}
        >
          {stats.needsAction.length === 0 ? (
            <PanelEmpty title="Niets openstaand" description="Alle aanvragen zijn opgepakt." />
          ) : (
            <>
              <RequestMiniList
                requests={stats.needsAction.slice(0, PANEL_LIMIT)}
                onOpen={(id) => navigate(`/aanvragen/${id}`)}
              />
              <PanelOverflow total={stats.needsAction.length} />
            </>
          )}
        </Panel>

        <Panel
          title="Dossiers op de wachtrij"
          count={stats.waitlist.length}
          action={<PanelLink to="/dossiers" label="Alle dossiers" />}
        >
          {stats.waitlist.length === 0 ? (
            <PanelEmpty title="Geen wachtrij" description="Er staan geen dossiers op de wachtrij." />
          ) : (
            <>
              <WaitlistList
                items={stats.waitlist.slice(0, PANEL_LIMIT)}
                onOpen={(id) => navigate(`/dossiers/${id}`)}
              />
              <PanelOverflow total={stats.waitlist.length} />
            </>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Inzetten die binnenkort aflopen"
          count={signals.expiring.length}
          icon={<AlertTriangle className="size-4 text-warning-600" aria-hidden />}
        >
          {signals.expiring.length === 0 ? (
            <PanelEmpty title="Niets vervalt binnenkort" description="Geen inzet loopt binnen 7 dagen af." />
          ) : (
            <>
              <SignalList
                signals={signals.expiring.slice(0, PANEL_LIMIT)}
                kind="expiry"
                onOpen={(id) => navigate(`/aanvragen/${id}`)}
              />
              <PanelOverflow total={signals.expiring.length} />
            </>
          )}
        </Panel>

        <Panel
          title="Inzetten die binnenkort starten"
          count={signals.startingSoon.length}
          icon={<CalendarClock className="size-4 text-info-600" aria-hidden />}
        >
          {signals.startingSoon.length === 0 ? (
            <PanelEmpty title="Niets gepland" description="Geen inzet start binnen 7 dagen." />
          ) : (
            <>
              <SignalList
                signals={signals.startingSoon.slice(0, PANEL_LIMIT)}
                kind="start"
                onOpen={(id) => navigate(`/aanvragen/${id}`)}
              />
              <PanelOverflow total={signals.startingSoon.length} />
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

function daysLabel(days: number): string {
  if (days === 0) return "vandaag";
  if (days === 1) return "over 1 dag";
  return `over ${days} dagen`;
}

function Panel({
  title,
  count,
  icon,
  action,
  children,
}: {
  title: string;
  count?: number;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <Card padding="none" className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-2">
          {icon}
          <CardTitle className="text-sm">{title}</CardTitle>
          {count !== undefined && count > 0 ? (
            <VBadge color="neutral" size="count">
              {count}
            </VBadge>
          ) : null}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

// Toont de rest-teller onder een afgeknipte lijst.
function PanelOverflow({ total }: { total: number }): React.JSX.Element | null {
  const hidden = total - PANEL_LIMIT;
  if (hidden <= 0) return null;
  return (
    <div className="border-t border-border px-5 py-2.5">
      <Caption tone="muted">+ {hidden} meer</Caption>
    </div>
  );
}

function PanelLink({ to, label }: { to: string; label: string }): React.JSX.Element {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline"
    >
      {label}
      <ArrowRight className="size-3.5" aria-hidden />
    </Link>
  );
}

function PanelEmpty({ title, description }: { title: string; description: string }): React.JSX.Element {
  return (
    <div className="p-5">
      <EmptyState title={title} description={description} className="py-8" />
    </div>
  );
}

function ListRow({
  onClick,
  title,
  meta,
  end,
}: {
  onClick: () => void;
  title: string;
  meta: string;
  end: ReactNode;
}): React.JSX.Element {
  return (
    <li>
      <VButton
        variant="ghost"
        fullWidth
        onClick={onClick}
        className="group h-auto items-center justify-start gap-4 rounded-none px-5 py-3 text-left font-normal hover:bg-muted/50 focus-visible:ring-inset focus-visible:ring-offset-0"
      >
        <span className="min-w-0 flex-1">
          <Body weight="semibold" className="truncate">
            {title}
          </Body>
          <Caption tone="muted" as="p" className="truncate">
            {meta}
          </Caption>
        </span>
        {end}
        <ChevronRight
          className="size-4 shrink-0 text-neutral-300 transition-colors group-hover:text-neutral-400"
          aria-hidden
        />
      </VButton>
    </li>
  );
}

function SignalList({
  signals,
  kind,
  onOpen,
}: {
  signals: ReadonlyArray<DashboardSignal>;
  kind: "expiry" | "start";
  onOpen: (id: string) => void;
}): React.JSX.Element {
  return (
    <ul className="divide-y divide-border">
      {signals.map((s) => {
        const urgent = kind === "expiry" && s.days <= 2;
        return (
          <ListRow
            key={`${s.requestId}-${s.assetNumber}-${kind}`}
            onClick={() => onOpen(s.requestId)}
            title={`${s.assetNumber} · ${s.title}`}
            meta={`${s.number} · ${s.period}`}
            end={
              <VBadge color={urgent ? "error" : kind === "expiry" ? "warning" : "info"}>
                {daysLabel(s.days)}
              </VBadge>
            }
          />
        );
      })}
    </ul>
  );
}

// Wachtrij-rijen op dossierniveau: toegekende periode en dossierstatus, met
// doorklik naar het dossierdetail (PZAO-30).
function WaitlistList({
  items,
  onOpen,
}: {
  items: ReadonlyArray<{ request: ReservationRequest; dossier: Dossier }>;
  onOpen: (dossierId: string) => void;
}): React.JSX.Element {
  return (
    <ul className="divide-y divide-border">
      {items.map(({ request, dossier }) => (
        <ListRow
          key={dossier.id}
          onClick={() => onOpen(dossier.id)}
          title={request.title}
          meta={`${request.number} · ${formatPeriod(dossier.startDate, dossier.endDate)}`}
          end={<DossierStatusBadge status={dossier.status} />}
        />
      ))}
    </ul>
  );
}

function RequestMiniList({
  requests,
  onOpen,
}: {
  requests: ReadonlyArray<ReservationRequest>;
  onOpen: (id: string) => void;
}): React.JSX.Element {
  return (
    <ul className="divide-y divide-border">
      {requests.map((r) => (
        <ListRow
          key={r.id}
          onClick={() => onOpen(r.id)}
          title={r.title}
          meta={`${r.number} · ${formatPeriod(r.startDate, r.endDate)}`}
          end={<RequestStatusBadge status={r.status} />}
        />
      ))}
    </ul>
  );
}
